import { clearAllListeners, type ThunkDispatch } from "@reduxjs/toolkit";
import { resetStateAction } from "redux/actions/resetState";
import { appStateSlice } from "redux/reducers/appStatesReducer";
import { companySlice } from "redux/reducers/companyReducer";
import { countrySlice } from "redux/reducers/countriesReducer";
import { clSlice } from "redux/reducers/creditLimitsReducer";
import { stepsSlice } from "redux/reducers/stepsReducer";
import EnhancedCacheClear from "./enhancedCacheClear";

import { accountrtk } from "api/api-accounts";
import { notes } from "api/api-accounts-notes-attachements";
import { aiForm } from "api/api-aiform";
import { audittrail } from "api/api-audittrail";
import { auth } from "api/api-auth";
import { banks } from "api/api-banks";
import { statusMasterApi } from "api/api-category-status";
import { comments } from "api/api-comments";
import { companiesrtk } from "api/api-company";
import { compliance } from "api/api-compliance";
import { configuration } from "api/api-configuration";
import { countries } from "api/api-countries";
import { creditCommittee } from "api/api-credit-committee";
import { emailsrtk } from "api/api-emails";
import { ewalletApi } from "api/api-ewallet";
import { finance } from "api/api-finance";
import { funder } from "api/api-funder";
import { guideline } from "api/api-guidelines";
import { human_resources } from "api/api-human-resources";
import { investor } from "api/api-investor";
import { invoiceFinanceApi } from "api/api-invoice-finance";
import { investor_v2 } from "api/api-invoices";
import { it } from "api/api-it";
import { leadComments } from "api/api-lead-comments";
import { legal } from "api/api-legal";
import { maps } from "api/api-maps";
import { marketing } from "api/api-marketing";
import { marketplace } from "api/api-marketplace";
import { misc } from "api/api-misc";
import { notifications } from "api/api-notifications";
import { operations } from "api/api-operations";
import { origination } from "api/api-origination";
import { postcc } from "api/api-post-cc";
import { risk } from "api/api-risk";
import { riskDashboardApi } from "api/api-risk-dashboard";
import { sales } from "api/api-sales";
import { statuses } from "api/api-status";
import { support } from "api/api-support";
import { users } from "api/api-users";
import { users_v2 } from "api/api-users-v2";
import { vendors } from "api/api-vendors";
import { vendorsv2 } from "api/api-vendorsv2";
import { invoiceOnboardingApi } from "api/invoice-onboarding-api";
import { cargoSlice } from "redux/reducers/cargoReducer";
import { experimentalFeaturesSlice } from "redux/reducers/experimentalFeaturesReducer";
import { invoiceSlice } from "redux/reducers/multipleInvoiceReducer";
import { searchSlice } from "redux/reducers/searchReducer";
import { sofrSlice } from "redux/reducers/sofrReducer";

export interface EnhancedClearStorageOptions {
  clearRedux?: boolean;
  clearRTKQuery?: boolean;
  clearBrowserStorage?: boolean;
  clearCache?: boolean;
  clearCookies?: boolean;
  clearServiceWorker?: boolean;
  reloadPage?: boolean;
}

const rtkApis = [
  accountrtk,
  aiForm,
  audittrail,
  auth,
  banks,
  comments,
  companiesrtk,
  compliance,
  configuration,
  countries,
  creditCommittee,
  emailsrtk,
  ewalletApi,
  finance,
  funder,
  guideline,
  human_resources,
  investor,
  investor_v2,
  invoiceFinanceApi,
  invoiceOnboardingApi,
  it,
  leadComments,
  legal,
  maps,
  marketing,
  marketplace,
  misc,
  notes,
  notifications,
  operations,
  origination,
  postcc,
  risk,
  riskDashboardApi,
  sales,
  statusMasterApi,
  statuses,
  support,
  users,
  users_v2,
  vendors,
  vendorsv2,
];

function resetSessionRedux(dispatch: ThunkDispatch<any, any, any>): void {
  dispatch(companySlice.actions.clearAll());
  dispatch(countrySlice.actions.clearAll());
  dispatch(stepsSlice.actions.clearSteps());
  dispatch(appStateSlice.actions.clearAppState());
  dispatch(clSlice.actions.clearAll());
  dispatch(cargoSlice.actions.clearShipments());
  dispatch(sofrSlice.actions.clearAll());
  dispatch(experimentalFeaturesSlice.actions.clearEFState());
  dispatch(invoiceSlice.actions.clearInvoices());
  dispatch(searchSlice.actions.clearAll());
  dispatch(resetStateAction());
  dispatch(clearAllListeners());
}

function resetRtkQueryCaches(dispatch: ThunkDispatch<any, any, any>): void {
  rtkApis.forEach((api) => {
    dispatch(api.util.resetApiState());
  });
}

/** Session-only reset. Does not unregister the service worker or wipe site data. */
export function clearAuthSession(
  dispatch: ThunkDispatch<any, any, any>
): void {
  // Remove account-specific browser data before another user can sign in.
  [
    "token",
    "email",
    "userId",
    "emailVerificationConfirmed",
    "fileUrlData",
    "BatchRefResult",
  ].forEach((key) => localStorage.removeItem(key));
  Object.keys(localStorage)
    .filter((key) =>
      ["incomlend:filters:", "gtc_approval_", "bl-verification:"].some(
        (prefix) => key.startsWith(prefix)
      )
    )
    .forEach((key) => localStorage.removeItem(key));
  Object.keys(sessionStorage)
    .filter(
      (key) =>
        key !== "vite-preload-recovery-attempted" &&
        !key.startsWith("app-update-reload:")
    )
    .forEach((key) => sessionStorage.removeItem(key));
  resetSessionRedux(dispatch);
  resetRtkQueryCaches(dispatch);
}

/**
 * Enhanced clear storage function that clears all application state and browser cache
 */
export async function enhancedClearStorage(
  dispatch: ThunkDispatch<any, any, any>,
  options: EnhancedClearStorageOptions = {}
): Promise<void> {
  const {
    clearRedux = true,
    clearRTKQuery = true,
    clearBrowserStorage = true,
    clearCache = true,
    clearCookies = true,
    clearServiceWorker = true,
    reloadPage = false
  } = options;

  if (clearRedux) {
    resetSessionRedux(dispatch);
  }

  if (clearRTKQuery) {
    resetRtkQueryCaches(dispatch);
  }

  // Clear browser storage and cache
  if (clearBrowserStorage || clearCache || clearCookies || clearServiceWorker) {
    await EnhancedCacheClear.clearAll({
      clearLocalStorage: clearBrowserStorage,
      clearSessionStorage: clearBrowserStorage,
      clearCookies: clearCookies,
      clearCacheStorage: clearCache,
      clearIndexedDB: clearCache,
      clearServiceWorker: clearServiceWorker,
      reloadPage: false // We'll handle reload separately
    });
  }

  // Reload page if requested
  if (reloadPage) {
    window.location.reload();
  }
}

/**
 * Quick cache clear function for development/debugging
 */
export async function quickCacheClear(): Promise<void> {
  try {
    await EnhancedCacheClear.clearAll({
      clearLocalStorage: true,
      clearSessionStorage: true,
      clearCookies: true,
      clearCacheStorage: true,
      clearIndexedDB: true,
      clearServiceWorker: true,
      reloadPage: true
    });
  } catch (error) {
    console.error('❌ Error during quick cache clear:', error);
    throw error;
  }
}

/**
 * Clear cache without reloading page
 */
export async function clearCacheWithoutReload(): Promise<void> {
  try {
    await EnhancedCacheClear.clearAll({
      clearLocalStorage: true,
      clearSessionStorage: true,
      clearCookies: true,
      clearCacheStorage: true,
      clearIndexedDB: true,
      clearServiceWorker: true,
      reloadPage: false
    });
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
    throw error;
  }
}

/**
 * Get current cache and storage information
 */
export async function getCacheInfo(): Promise<{
  localStorage: number;
  sessionStorage: number;
  cookies: number;
  cacheStorage: string[];
  serviceWorkers: number;
}> {
  try {
    const info = await EnhancedCacheClear.getCacheInfo();
    return info;
  } catch (error) {
    console.error('❌ Error getting cache info:', error);
    throw error;
  }
}
